<template>
    <message-hub-sidebar
        :items="items"
        :loading="loading"
        :error="error"
        :emptyMessage="filters.search ? 'No threads found matching your search.' : 'No results found'"
        @retry="fetchFirstPage()"
        @scroll="handleScroll"
    >
        <template v-slot:filters>
            <template v-if="!bulkSelection">
                <vx-filterable
                    @click="toggleFilterable($event)"
                    :active="filterableDropdown"
                    appearance="input"
                    :placement="isMd ? 'bottom' : 'top'"
                    size="md"
                    data-test="filters"
                >
                    <template v-slot:placeholder>
                        <div class="flex justify-between w-full gap-1 pr-2">
                            <span>
                                <span class="mr-2">Find</span>
                                <vx-badge
                                    v-if="filtersCount > 0"
                                    data-test="reset"
                                    :borders="false"
                                    size="xs"
                                    @click.stop="clearAllFilters"
                                >
                                    {{ pluralized('filter', filtersCount, true) }}
                                    <font-awesome-icon widthAuto :icon="faCircleXmark" />
                                </vx-badge>
                            </span>
                            <span class="flex items-center text-sm gap-0.5 text-slate-300">
                                <template v-if="usesCommandKey">
                                    <font-awesome-icon widthAuto :icon="faCommand"></font-awesome-icon>
                                </template>
                                <template v-else> Ctrl </template>
                                <font-awesome-icon widthAuto :icon="faSquareK"></font-awesome-icon>
                            </span>
                        </div>
                    </template>
                    <template v-slot:form>
                        <message-hub-threads-sidebar-filters
                            v-model="filters"
                            @apply="closeFilterable"
                            @cancel="closeFilterable"
                        ></message-hub-threads-sidebar-filters>
                    </template>
                </vx-filterable>
                <vx-modal v-model:visible="filterableModal" size="sm" :padding="false">
                    <template v-slot:header>
                        <div class="p-6 bg-white">Filters</div>
                    </template>

                    <message-hub-threads-sidebar-filters
                        v-if="filterableModal"
                        appearance="modal"
                        v-model="filters"
                        @apply="closeFilterable"
                        @cancel="closeFilterable"
                    ></message-hub-threads-sidebar-filters>
                </vx-modal>
            </template>

            <message-hub-bulk-actions-bar v-if="mobileBulkMode && !isMd" />
        </template>

        <!-- Filter Items -->
        <template v-if="!bulkSelection" v-slot:filterItems>
            <message-hub-sidebar-sort-item
                @click="forward = true"
                :active="forward"
                :icon="faArrowUp"
                data-test="oldest-first"
            >
                Oldest First
            </message-hub-sidebar-sort-item>

            <message-hub-sidebar-sort-item
                @click="forward = false"
                :active="!forward"
                :icon="faArrowDown"
                data-test="newest-first"
            >
                Newest First
            </message-hub-sidebar-sort-item>

            <template v-if="FEATURES.simple_message_hub">
                <message-hub-sidebar-sort-item
                    @click="status = undefined"
                    :active="!status"
                    :icon="null"
                    data-test="filter-all"
                    class="border-t-2"
                >
                    All
                </message-hub-sidebar-sort-item>

                <message-hub-sidebar-sort-item
                    @click="status = 'open'"
                    :active="status === 'open'"
                    :icon="faEnvelope"
                    data-test="filter-unread"
                >
                    Unread
                </message-hub-sidebar-sort-item>

                <message-hub-sidebar-sort-item
                    @click="status = 'closed'"
                    :active="status === 'closed'"
                    :icon="faEnvelopeOpen"
                    data-test="filter-read"
                >
                    Read
                </message-hub-sidebar-sort-item>
            </template>
        </template>

        <!-- Action Buttons -->
        <template v-if="!mobileBulkMode" v-slot:actionButtons>
            <div v-if="bulkSelection" class="hidden justify-end items-center gap-2 w-full min-h-[42px] md:flex">
                <vx-button size="md" color="muted" @click="selectThreads()" data-test="select-all">
                    Select All
                    <vx-badge color="slate" size="xs" class="text-[10px] mix-blend-multiply h-4! px-1.5! pt-0.5!">
                        {{ MAX_SELECTED_THREADS_NUMBER }} Max
                    </vx-badge>
                </vx-button>
                <vx-button size="md" color="muted" @click="clearSelection()" data-test="clear-selected">
                    Clear
                </vx-button>
                <router-button
                    :to="{
                        name: 'message-hub',
                        query: route.query,
                    }"
                    size="md"
                    color="secondary"
                    data-test="close-select"
                >
                    <font-awesome-icon widthAuto :icon="faXmark" class="text-white"></font-awesome-icon>
                </router-button>
            </div>
            <template v-else>
                <router-button
                    :to="{
                        name: 'message-hub.bulk-actions',
                        query: route.query,
                    }"
                    size="md"
                    :color="bulkSelection ? 'secondary' : 'muted'"
                    class="hidden! md:flex!"
                    data-test="toggle-select"
                >
                    <font-awesome-icon widthAuto :icon="faCheckDouble"></font-awesome-icon>
                </router-button>
                <router-button
                    :to="{ name: 'message-hub.new-message', query: route.query }"
                    size="md"
                    color="primary"
                    class="md:hidden mdShortHeight:flex"
                >
                    <font-awesome-icon widthAuto :icon="faMessagePlus"></font-awesome-icon>
                </router-button>
            </template>
        </template>

        <!-- Custom Alerts -->
        <template v-slot:alerts>
            <vx-alert v-if="!shouldProcessQueue" :closeable="false" color="info">
                <div class="flex items-center justify-between">
                    <div class="grow">
                        New messages <span class="font-extrabold">will not</span> show while filters are applied.
                    </div>
                    <vx-button @click="clearAllFilters()" color="info" size="sm">
                        Clear
                        <font-awesome-icon widthAuto :icon="faUndo"></font-awesome-icon>
                    </vx-button>
                </div>
            </vx-alert>
        </template>

        <!-- Mobile Header -->
        <template v-slot:mobileHeader>
            <div
                class="flex gap-2 justify-between"
                :class="{ 'items-start flex-col @lg/mh-sidebar:flex-row @lg/mh-sidebar:items-center': mobileBulkMode }"
            >
                <div class="flex flex-row gap-2">
                    <h1 class="text-2xl mb-0 font-extrabold">Threads</h1>
                    <span v-if="mobileBulkMode" class="text-2xl mb-0 whitespace-nowrap">
                        {{ selectedThreads.size }} selected
                    </span>
                </div>

                <div :class="{ 'flex gap-2 items-center w-full @lg/mh-sidebar:w-auto': mobileBulkMode }">
                    <vx-button
                        v-if="mobileBulkMode"
                        size="md"
                        color="muted-light"
                        @click="clearSelection()"
                        data-test="clear-selected"
                        class="w-full @lg/mh-sidebar:w-auto"
                    >
                        Clear Selection
                        <font-awesome-icon widthAuto :icon="faUndo"></font-awesome-icon>
                    </vx-button>
                    <vx-dropdown teleport placement="right">
                        <vx-button color="muted-light" size="md">
                            <font-awesome-icon widthAuto :icon="faEllipsis"></font-awesome-icon>
                        </vx-button>
                        <template v-slot:items>
                            <vx-dropdown-item @click.prevent="toggleMobileBulkMode">
                                Multi Select
                                <font-awesome-icon widthAuto :icon="faCheckDouble"></font-awesome-icon>
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>
                </div>
            </div>
        </template>

        <!-- Content List -->
        <template v-slot:content="{ items }">
            <message-hub-thread-sidebar-row
                v-for="(item, index) in items"
                :key="item.contact_id"
                :thread="item"
                :bulkSelection="bulkSelection"
                @click="handleThreadClick($event, item, index)"
            ></message-hub-thread-sidebar-row>
        </template>
    </message-hub-sidebar>
</template>

<script setup>
import MessageHubThreadsSidebarFilters from './MessageHubThreadsSidebarFilters.vue';
import { faEnvelope, faEnvelopeOpen } from '@fortawesome/pro-regular-svg-icons';
import {
    faArrowDown,
    faArrowUp,
    faCheckDouble,
    faCommand,
    faEllipsis,
    faMessagePlus,
    faSquareK,
    faUndo,
    faCircleXmark,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxFilterable,
    VxModal,
} from '@voxie/frontend-components';
import axios from 'axios';
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import RouterButton from '~/components/general/RouterButton.vue';
import MessageHubSidebarSortItem from '~/components/message-hub/message-hub-sidebar/MessageHubSidebarSortItem.vue';
import { getStatusForInbox } from '~/components/message-hub/messageHubUtil';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import debounce from '~/utils/debounce';
import parseLinkHeader from '~/utils/parseLinkHeader';
import { isLargeTeam } from '~/utils/team';
import MessageHubThreadSidebarRow from './MessageHubThreadSidebarRow.vue';
import { useConversation } from '~/composables/useConversation';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import MessageHubSidebar from '~/components/message-hub/message-hub-sidebar/MessageHubSidebar.vue';
import { useEventListener } from '@vueuse/core';
import { MAX_SELECTED_THREADS_NUMBER } from '~/constants/messageHub';
import { pluralized } from '~/components/filters';
import Bus from '~/globals/eventBus';
import { threadsClient } from '~/services';
import MessageHubBulkActionsBar from '~/components/message-hub/MessageHubBulkActionsBar.vue';

const { isMd } = useResponsiveLayout();

const route = useRoute();
const router = useRouter();
const teamId = Spark.state.currentTeam.id;
const currentUserId = Spark.state.user.id;
const { onCorporate, getCurrentGroupId } = useCurrentGroup();

const usesCommandKey = /iPad|iPhone|Mac OS/.test(navigator.userAgent);

const {
    currentInbox,
    toggleThreadSelection,
    isThreadSelected,
    clearSelection,
    removeThreadSelection,
    selectedThreads,
} = useMessageHub();
const {
    activeThread,
    isPendingUndoReselection,
    removePendingUndoReselection,
    mount: mountConversation,
} = useConversation();

const error = ref(false);

const selectable = ref(false);
const mobileBulkMode = ref(false);

const defaultFilters = () => ({
    search: '',
    tag: '',
});

const filters = ref(defaultFilters());
const forward = ref(false);
const filterableDropdown = ref(false);
const filterableModal = ref(false);

const filtersCount = computed(() => (filters.value.search ? 1 : 0) + (filters.value.tag ? 1 : 0));

const FEATURES = inject('FEATURES');
const loading = ref(true);

const items = ref([]);

const defaultStatus = computed(() => getStatusForInbox(currentInbox.value));
const status = ref(getStatusForInbox(currentInbox.value));

const bulkSelection = computed(() => route.name === 'message-hub.bulk-actions' || mobileBulkMode.value);

watch(defaultStatus, () => (status.value = defaultStatus.value));

let linkHeader;
let fetchThreadsAbort;

const fetchFirstPage = async () => {
    fetchThreadsAbort?.abort();
    fetchThreadsAbort = new AbortController();

    loading.value = true;
    error.value = false;
    items.value = [];

    const searchKey = isLargeTeam(teamId) ? (filters.value.search.includes('@') ? 'email' : 'phone') : 'search';

    const params = {
        status: status.value,
        forward: forward.value ? 'true' : 'false',
        assignee: currentInbox.value === 'me' ? Spark.state.user.id : undefined,
    };

    if (filters.value.tag !== '') {
        params.tag = filters.value.tag;
    }

    if (filters.value.search !== '') {
        params[searchKey] = filters.value.search;
    }

    try {
        const result = onCorporate()
            ? await threadsClient.threadsGetAll(teamId, params, { signal: fetchThreadsAbort.signal })
            : await threadsClient.groupThreadsGetAll(teamId, getCurrentGroupId(), params, {
                  signal: fetchThreadsAbort.signal,
              });
        items.value = result.data.data;
        linkHeader = result.headers.link;
        loading.value = false;
    } catch (e) {
        if (!axios.isCancel(e)) {
            console.error(e);
            error.value = true;
            loading.value = false;
        }
    }
};

watch([forward, currentInbox, status, filters], debounce(fetchFirstPage, 150), {
    deep: true,
    immediate: true,
});

const clearAllFilters = () => {
    forward.value = false;
    status.value = defaultStatus.value;
    items.value = [];

    filters.value = defaultFilters();

    if (bulkSelection.value) {
        clearSelection();
    }
};

const closeFilterable = async () => {
    filterableDropdown.value = false;
    filterableModal.value = false;
};

const toggleFilterable = (event) => {
    event?.preventDefault?.();
    if (isMd.value) {
        filterableDropdown.value = !filterableDropdown.value;
    } else {
        filterableModal.value = !filterableModal.value;
    }
};

const fetchMoreThreads = async () => {
    if (loading.value) {
        return;
    }

    loading.value = true;

    const links = parseLinkHeader(linkHeader);

    const expectedLink = forward.value ? 'next' : 'prev';

    if (!links?.[expectedLink]?.url) {
        loading.value = false;
        return;
    }

    try {
        // Assuming this will use group url from another request.
        const result = await threadsClient.threadsListUsingLink(links[expectedLink].url);
        linkHeader = result.headers.link;
        items.value = items.value.concat(result.data.data);

        return result;
    } finally {
        loading.value = false;
    }
};

/**
 * @param {Event} event
 */
const handleScroll = (event) => {
    if (!event.target) {
        return;
    }

    const el = event.target;

    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const height = el.offsetHeight;
    const threshold = window.innerHeight * 0.5;
    if (scrollTop + height >= scrollHeight - threshold) {
        fetchMoreThreads();
    }
};

// logic is the same to the one in voxie/mh-lite:src/screens/threads/threads-inbox/threads-inbox.screen.tsx
// if there's a bug that needs to be fixed here we might need to fix in the mobile app too
let itemsQueue = [];
const shouldProcessQueue = computed(() => {
    return forward.value === false && status.value === defaultStatus.value && !filtersCount.value && !selectable.value;
});

const processItemsQueue = () => {
    if (!shouldProcessQueue.value || bulkSelection.value) {
        return;
    }

    if (itemsQueue.length === 0) {
        return;
    }

    // take 10 items to process at a time
    const processItems = itemsQueue.splice(0, 10);

    processItems.forEach(({ action, thread }) => {
        const threadIndex = items.value.findIndex((existing) => existing.contact_id === thread.contact_id);

        if (action === 'delete' && threadIndex !== -1) {
            if (activeThread.value && activeThread.value.contact_id === thread.contact_id) {
                selectNextThread(thread);
            }

            items.value.splice(threadIndex, 1);
        } else if (action === 'upsert') {
            if (threadIndex === -1) {
                // create - thread is being added to the inbox
                items.value.unshift(thread);

                // Check if this thread should be reselected after undo
                if (isPendingUndoReselection(thread.contact_id, currentInbox.value)) {
                    // Reselect this thread as it's returning from an undo operation
                    router.replace({
                        name:
                            route.name === 'message-hub.contact-view'
                                ? 'message-hub.contact-view'
                                : 'message-hub.conversation',
                        params: { contactId: thread.contact_id },
                        query: route.query,
                    });
                    // Remove from pending list since we've handled the reselection
                    removePendingUndoReselection(thread.contact_id);
                }
            } else {
                // update
                const updatedThread = { ...items.value[threadIndex], ...thread };
                if (items.value[threadIndex].last_message_id === thread.last_message_id) {
                    // when its the same last message id we keep it at the same sorting position
                    items.value[threadIndex] = updatedThread;
                } else {
                    // when the last message id is different we put it at the top of the list
                    // note that forward prevents processing in real time, so we only care
                    // about putting item at the top of the list, not at the bottom
                    items.value.splice(threadIndex, 1);
                    items.value.unshift(updatedThread);
                }
            }
        }
    });
};

const lastSelectedIndex = ref(0);

const getRangeStats = (startIndex, endIndex) => {
    const start = Math.max(0, startIndex);
    const end = Math.min(items.value.length - 1, endIndex);

    const selected = [];
    const unselected = [];

    for (let i = start; i <= end; i++) {
        const thread = items.value[i];
        if (!thread) {
            continue;
        }

        if (isThreadSelected(thread.contact_id)) {
            selected.push(i);
        } else {
            unselected.push(i);
        }
    }

    return { start, end, selected, unselected };
};

const toggleRangeSelection = (startIndex, endIndex) => {
    const { start, end, selected, unselected } = getRangeStats(startIndex, endIndex);

    const total = end - start + 1;
    const allSelected = selected.length === total;

    if (allSelected || unselected.length === 1) {
        // Deselect currently selected items in range
        for (const i of selected) {
            const thread = items.value[i];
            if (thread) {
                removeThreadSelection(thread);
            }
        }
    } else {
        // Select missing items in range
        for (let i = start; i <= end; i++) {
            const thread = items.value[i];
            if (thread && !isThreadSelected(thread.contact_id)) {
                toggleThreadSelection(thread);
            }
        }
    }
};

const handleThreadClick = (event, thread, index) => {
    if (bulkSelection.value) {
        if (event.shiftKey) {
            const startIndex = Math.min(lastSelectedIndex.value, index);
            const endIndex = Math.max(lastSelectedIndex.value, index);

            toggleRangeSelection(startIndex, endIndex);
        } else {
            toggleThreadSelection(thread);
        }

        lastSelectedIndex.value = index;
    } else {
        // uses the thread to prefill the contact
        mountConversation(thread);
    }
};

const selectThreads = (startIndex = 0, endIndex = MAX_SELECTED_THREADS_NUMBER - 1) => {
    endIndex = items.value.length - 1 < endIndex ? items.value.length - 1 : endIndex;

    for (let i = startIndex; i <= endIndex; i++) {
        if (!isThreadSelected(items.value[i].contact_id)) {
            toggleThreadSelection(items.value[i]);
        }
    }
};

const handleThreadUpdated = ({ thread }) => {
    const inbox = currentInbox.value;
    let action = 'upsert';

    if (inbox === 'closed') {
        action = thread.status === 'closed' ? 'upsert' : 'delete';
    } else if (inbox === 'open') {
        action = thread.status === 'open' ? 'upsert' : 'delete';
    } else if (inbox === 'unassigned') {
        action = !thread.assignee_id && thread.status === 'open' ? 'upsert' : 'delete';
    } else if (inbox === 'me') {
        action = thread.assignee_id === currentUserId && thread.status === 'open' ? 'upsert' : 'delete';
    }

    itemsQueue.push({ action, thread: thread });
};

const selectNextThread = (currentThread) => {
    const currentIndex = items.value.findIndex((t) => t.contact_id === currentThread.contact_id);
    // Try thread below first, then above
    const nextThread = items.value[currentIndex + 1] || items.value[currentIndex - 1];

    if (nextThread) {
        router.replace({
            name: route.name === 'message-hub.contact-view' ? 'message-hub.contact-view' : 'message-hub.conversation',
            params: { contactId: nextThread.contact_id },
            query: route.query,
        });
    } else {
        // No threads available, go to empty state
        router.replace({ name: 'message-hub', query: route.query });
    }
};

let queueInterval;
// when filters are changed the queue is cleared.
watch(
    shouldProcessQueue,
    () => {
        clearInterval(queueInterval);
        itemsQueue = [];

        // run the queue every 10ms
        queueInterval = setInterval(processItemsQueue, 10);
    },
    {
        immediate: true,
    }
);

// Synchronize changes made to activeThread with the items list.
// This ensures the sidebar reflects updates such as status changes (open/closed),
// read/unread state, or assignee modifications in real time.
watch(
    activeThread,
    (currentActiveThread, previousActiveThread) => {
        if (!currentActiveThread || !previousActiveThread) {
            return;
        }

        if (currentActiveThread.contact_id !== previousActiveThread.contact_id) {
            return;
        }

        const threadIndex = items.value.findIndex((thread) => thread.contact_id === currentActiveThread.contact_id);
        if (threadIndex === -1) {
            return;
        }

        items.value[threadIndex] = {
            ...items.value[threadIndex],
            ...currentActiveThread,
        };
    },
    {
        deep: true,
    }
);

let echoChannel;
onMounted(() => {
    const channelName = onCorporate() ? `messages.${teamId}` : `messages.${teamId}.${getCurrentGroupId()}`;
    echoChannel = window.Echo.private(channelName);

    echoChannel.listen('.ThreadUpdated', handleThreadUpdated);
    Bus.$on('threadUpdated', handleThreadUpdated);
});

// cleanup listener and watchers
onUnmounted(() => {
    clearInterval(queueInterval);
    echoChannel.stopListening('.ThreadUpdated', handleThreadUpdated);
    Bus.$off('threadUpdated', handleThreadUpdated);
    mobileBulkMode.value = false;
});

useEventListener(window, 'keydown', (event) => {
    if ((event.metaKey && event.key === 'k') || (event.ctrlKey && event.key === 'k')) {
        event.preventDefault();
        toggleFilterable(true);
    }

    if (bulkSelection.value && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        selectThreads();
    }
});

const toggleMobileBulkMode = () => {
    mobileBulkMode.value = !mobileBulkMode.value;
};

watch(isMd, (newValue) => {
    if (newValue) {
        mobileBulkMode.value = false;
    }
});

watch(mobileBulkMode, (newValue) => {
    if (!newValue) {
        clearSelection();
    }
});
</script>
